import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { MinusCircleIcon } from '@heroicons/react/24/outline';
import Form from '@dashboard/components/Formik/Form';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { fetcher } from '@dashboard/services/request';
import SettingsContainer from '@dashboard/components/SettingsContainer';
import SettingsHeader from '@dashboard/components/SettingsHeader';
import SectionHeading from '@dashboard/components/SectionHeading';
import { toast } from '@dashboard/context/ToastContext';
import { getBillingPeriod } from '@dashboard/services/plans';
import { sellerKey, sellerUsageKey } from '@dashboard/lib/queryKeys';
import Dialog from '@dashboard/components/Dialog';
import Button from '@dashboard/components/Button';
import { Badge } from '@dashboard/components/ui/badge';
import { tracking } from '@dashboard/lib/tracking';
import FreePlanChanges, {
  FREE_PLAN_CAP_NOTE,
  getFreePlanChanges,
} from '@dashboard/components/FreePlanChanges/FreePlanChanges';

const FREE_PLAN_KEEPS = [
  'Your store stays live and you keep selling',
  'Unlimited checkout pages, events and forms',
  'All your products, customers and payment history',
  'Up to $3K in monthly sales',
];

const FREE_PLAN_CANCEL_EFFECTS = [
  'Your store goes offline: checkout pages, events and forms stop accepting payments and submissions',
  'Your storefront and payment links stop working',
];

const CancelSubscription = () => {
  const { seller, plan, sellerId, token, setSeller } = useAuth();
  const [shouldPollSeller, setShouldPollSeller] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  const history = useHistory();
  const queryClient = useQueryClient();

  const isFreePlan = seller.subscriptionStatus === 'free';

  const renewsOn = seller.stripeSubscriptionCurrentPeriodEnd
    ? format(new Date(seller.stripeSubscriptionCurrentPeriodEnd), 'd MMM, yyyy')
    : null;

  const { data: updatedSeller } = useQuery({
    queryKey: sellerKey(sellerId),
    queryFn: () => fetcher(`/sellers/${sellerId}`, token),
    enabled: shouldPollSeller,
    refetchInterval: 1000,
  });

  const cancelSubscriptionMutation = useMutation({
    mutationFn: ({ cancelReason: reason }) =>
      fetcher(`/sellers/${sellerId}/cancel-subscription`, token, {
        method: 'POST',
        body: JSON.stringify({ cancelReason: reason, downgradeOfferShown: !isFreePlan }),
      }),
    onSuccess: updatedSellerData => {
      if (isFreePlan) {
        // Free cancel is immediate: no Stripe period to wait for, so no polling.
        setSeller(updatedSellerData);
        queryClient.invalidateQueries({ queryKey: sellerKey(sellerId) });
        queryClient.invalidateQueries({ queryKey: sellerUsageKey(sellerId) });
        toast({
          title: 'Free plan canceled',
          description: 'Your store is now offline. You can start a new plan anytime.',
        });
        setModalOpen(false);
        history.push('/settings/plan');
        return;
      }

      // Paid cancels always see the Stay on Free offer in the confirm dialog,
      // so completing the cancel means the offer was rejected. Counterpart of
      // free_plan_taken_at_cancel; together they form the true save-rate denominator.
      tracking.trackEvent('free_offer_rejected_at_cancel');

      toast({
        title: 'Subscription canceled',
        description: 'Your subscription has been canceled',
      });
      setShouldPollSeller(true);
    },
    onError: () => {
      toast({
        title: 'Something went wrong',
        description: 'Something went wrong cancelling your subscription. Please try again.',
        type: 'error',
      });
    },
  });

  const downgradeToFreeMutation = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/downgrade-to-free`, token, {
        method: 'POST',
        body: JSON.stringify({ source: 'cancel_flow', cancelReason }),
      }),
    onSuccess: updatedSellerData => {
      setSeller(updatedSellerData);
      queryClient.invalidateQueries({ queryKey: sellerKey(sellerId) });
      queryClient.invalidateQueries({ queryKey: sellerUsageKey(sellerId) });
      tracking.trackEvent('free_plan_taken_at_cancel');
      const switchesOn =
        updatedSellerData?.pendingFreeDowngrade && updatedSellerData?.stripeSubscriptionCancelAt
          ? format(new Date(updatedSellerData.stripeSubscriptionCancelAt), 'd MMM, yyyy')
          : null;
      toast({
        title: switchesOn ? 'Downgrade to Free scheduled' : 'Switched to Free plan',
        description: switchesOn
          ? `You keep your paid features until ${switchesOn}, then your store moves to Free.`
          : 'Your account has been moved to the Free plan',
      });
      setModalOpen(false);
      history.push('/settings/plan');
    },
    onError: () => {
      toast({
        title: 'Something went wrong',
        description: 'Something went wrong switching to Free. Please try again.',
        type: 'error',
      });
    },
  });

  useEffect(() => {
    if (updatedSeller?.stripeSubscriptionCancelAt) {
      setSeller(updatedSeller);

      history.push('/settings/plan');
    }
  }, [updatedSeller]);

  const freePlanChanges = getFreePlanChanges(seller);

  // The API schedules the downgrade at period end only when a Stripe subscription
  // exists (same field it branches on); without one it downgrades immediately, so
  // don't promise a date off a stale stripeSubscriptionCurrentPeriodEnd.
  const switchesOn = seller.stripeSubscriptionId ? renewsOn : null;

  if (isFreePlan) {
    return (
      <SettingsContainer size="2xl">
        <SettingsHeader>Cancel Free plan</SettingsHeader>

        <p className="text-sm text-gray-600 dark:text-muted-foreground">
          You&rsquo;re on the Free plan, so you&rsquo;re not paying anything right now.
        </p>

        <div className="!mt-4">
          <p className="text-sm text-gray-600 dark:text-muted-foreground">
            Cancelling takes effect immediately:
          </p>
          <ul className="flex flex-col gap-y-1.5 text-sm text-gray-600 dark:text-muted-foreground mt-2">
            {FREE_PLAN_CANCEL_EFFECTS.map(effect => (
              <li key={effect} className="flex items-start gap-x-1.5">
                <MinusCircleIcon className="shrink-0 w-4 h-4 mt-0.5 text-gray-300 dark:text-muted-foreground" />
                <span>{effect}</span>
              </li>
            ))}
          </ul>
          <p className="text-sm text-gray-600 dark:text-muted-foreground mt-2">
            We don&rsquo;t delete anything. Your pages, products, customers and payment history will
            be here if you come back.
          </p>
        </div>

        <div className="!mt-8">
          <Form
            showState={false}
            initialValues={{
              cancelReason: '',
            }}
            fields={[
              {
                name: 'cancelReason',
                label: 'Let us know why you’re leaving',
                placeholder: 'Your feedback',
                type: 'textarea',
                required: true,
              },
            ]}
            submitButton={{
              label: 'Cancel free plan',
              appearance: 'light',
              size: 'base',
            }}
            cancelButton={{
              label: 'Back',
              appearance: 'link',
              size: 'base',
              onClick: () => history.push('/settings/plan'),
            }}
            onSubmit={async values => {
              setCancelReason(values.cancelReason);
              setModalOpen(true);
              return { data: true };
            }}
          />
        </div>

        <Dialog
          open={modalOpen}
          onCancel={() => setModalOpen(false)}
          title="Cancel your Free plan?"
          cancelButton={null}
          confirmButton={null}
          size="md"
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-muted-foreground">
              Your store goes offline immediately: checkout pages, events and forms stop accepting
              payments and submissions. We keep your account and data, so you can pick up where you
              left off if you come back.
            </p>

            <div className="flex flex-col gap-3 pt-2">
              <Button appearance="primary" size="base" onClick={() => setModalOpen(false)}>
                Keep my store live
              </Button>

              <button
                type="button"
                className="text-sm text-gray-500 dark:text-muted-foreground underline hover:text-gray-700 dark:hover:text-muted-foreground text-center disabled:opacity-50"
                disabled={cancelSubscriptionMutation.isPending}
                onClick={async () => {
                  try {
                    await cancelSubscriptionMutation.mutateAsync({ cancelReason });
                  } catch {
                    // onError shows the error toast; catch prevents unhandled-rejection noise
                  }
                }}
              >
                Cancel anyway
              </button>
            </div>
          </div>
        </Dialog>
      </SettingsContainer>
    );
  }

  return (
    <SettingsContainer size="2xl">
      <SettingsHeader>Cancel subscription</SettingsHeader>

      <p className="text-sm text-gray-600 dark:text-muted-foreground">
        You&rsquo;re on {plan.title} ({getBillingPeriod(plan.billingPeriod)})
        {renewsOn && (
          <>
            , renews on{' '}
            <span className="font-medium text-gray-900 dark:text-foreground">{renewsOn}</span>
          </>
        )}
        .
      </p>

      <div className="border border-input rounded-lg bg-card p-5 !mt-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-foreground">
              Keep selling on the Free plan
            </h3>
            <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
              Downgrade instead of canceling. Your store stays live, and selling stays free up to
              $3K per month.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0 whitespace-nowrap">
            $0 / month
          </Badge>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 border-t border-input mt-4 pt-3">
          <div>
            <SectionHeading>What you keep</SectionHeading>
            <ul className="flex flex-col gap-y-1.5 text-sm text-gray-600 dark:text-muted-foreground mt-2">
              {FREE_PLAN_KEEPS.map(item => (
                <li key={item} className="flex items-start gap-x-1.5">
                  <CheckCircleIcon className="shrink-0 w-4 h-4 mt-0.5 text-foreground" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <SectionHeading>
              {switchesOn ? `What changes on ${switchesOn}` : 'What changes'}
            </SectionHeading>
            <ul className="flex flex-col gap-y-1.5 text-sm text-gray-600 dark:text-muted-foreground mt-2">
              {freePlanChanges.map(change => (
                <li key={change} className="flex items-start gap-x-1.5">
                  <MinusCircleIcon className="shrink-0 w-4 h-4 mt-0.5 text-gray-300 dark:text-muted-foreground" />
                  <span>{change}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <p className="text-sm text-gray-500 dark:text-muted-foreground border-t border-input mt-4 pt-3">
          {FREE_PLAN_CAP_NOTE}
        </p>

        <div className="flex items-center gap-3 mt-4">
          <Button
            appearance="primary"
            size="base"
            disabled={downgradeToFreeMutation.isPending}
            onClick={() => downgradeToFreeMutation.mutate()}
          >
            {downgradeToFreeMutation.isPending ? 'Switching…' : 'Switch to Free'}
          </Button>
          <span className="text-sm text-gray-500 dark:text-muted-foreground">
            {switchesOn
              ? `You keep your paid features until ${switchesOn}. Upgrade again anytime.`
              : 'Takes effect immediately. Upgrade again anytime.'}
          </span>
        </div>
      </div>

      <div className="!mt-10">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-foreground">
          Still want to cancel?
        </h3>
        <p className="text-sm text-gray-600 dark:text-muted-foreground mt-1">
          Your subscription stays active until the end of your billing period
          {renewsOn && (
            <>
              {' '}
              on <span className="font-medium text-gray-900 dark:text-foreground">{renewsOn}</span>
            </>
          )}
          . We don&rsquo;t delete anything, so everything will be here if you come back.
        </p>

        <div className="mt-4">
          <Form
            showState={false}
            initialValues={{
              cancelReason: '',
            }}
            fields={[
              {
                name: 'cancelReason',
                label: 'Let us know why you’re leaving',
                placeholder: 'Your feedback',
                type: 'textarea',
                required: true,
              },
            ]}
            submitButton={{
              label: 'Cancel subscription',
              appearance: 'light',
              size: 'base',
              disabled: shouldPollSeller,
            }}
            cancelButton={{
              label: 'Back',
              appearance: 'link',
              size: 'base',
              onClick: () => history.push('/settings/plan'),
            }}
            onSubmit={async values => {
              setCancelReason(values.cancelReason);
              setModalOpen(true);
              return { data: true };
            }}
          />
        </div>
      </div>

      <Dialog
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        title="Cancel your subscription?"
        cancelButton={null}
        confirmButton={null}
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-muted-foreground">
            Fully cancelling takes your store offline{renewsOn ? ` on ${renewsOn}` : ''}. If you
            stay on Free instead, your store keeps running
            {switchesOn ? ' and you keep your paid features until then' : ''}.
          </p>
          <FreePlanChanges seller={seller} />

          <div className="flex flex-col gap-3 pt-2">
            <Button
              appearance="primary"
              size="base"
              disabled={downgradeToFreeMutation.isPending}
              onClick={() => downgradeToFreeMutation.mutate()}
            >
              Stay on Free
            </Button>

            <button
              type="button"
              className="text-sm text-gray-500 dark:text-muted-foreground underline hover:text-gray-700 dark:hover:text-muted-foreground text-center disabled:opacity-50"
              disabled={cancelSubscriptionMutation.isPending || shouldPollSeller}
              onClick={async () => {
                try {
                  await cancelSubscriptionMutation.mutateAsync({ cancelReason });
                  setModalOpen(false);
                } catch {
                  // onError shows the error toast; catch prevents unhandled-rejection noise
                }
              }}
            >
              Cancel anyway
            </button>
          </div>
        </div>
      </Dialog>
    </SettingsContainer>
  );
};

export default CancelSubscription;
